import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import {
  hydraScanStartAction,
  pushNotificationAction,
} from 'actions/app-actions';
import { getAllPaxPaymentMethodsV2 } from 'actions/booking-actions';
import endpoints from 'actions/booking-endpoints';
import {
  ROUTE_OPTIMIZE_STATUS_VALUE,
  SCRIBE_REGULATION_EVENTS,
} from 'applications/scribe/instrumentations/track';
import fetchObservableUtil from 'common/utils/fetch-observable';
import { getDefaultErrorContext } from 'common/utils/rum-utils';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import { ErrorCodes } from 'constants/anytime-booking-constants';
import {
  BookingMode,
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
} from 'constants/booking-constants';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { fetchAddressV2 } from 'helper/address-search';
import { buildAjaxCredentials, buildHeadersV2 } from 'helper/ajax-helper';
import {
  ajaxFetchDeliveriesQuote,
  deliveriesToCreateDeliveriesV2Payload,
  getOfferID,
  getOfferType,
  isAutoApplyOfferType,
  isRewardOffer,
} from 'helper/anytime-booking';
import { isAreaZoningError, isAreaZoningSoftBlock } from 'helper/error-codes';
import { savePickUpPOIInStorage } from 'helper/storage';
import I18n from 'i18n-js';
import _countBy from 'lodash/countBy';
import _every from 'lodash/every';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, debounceTime, mergeMap } from 'rxjs/operators';
import {
  paymentMethodSelector,
  serviceTypesInCitySelector,
} from 'selectors/booking-selectors';
import {
  allDeliveriesSelector,
  bookingConfigurationSelector,
  deliveriesFromPropsSelector,
  deliverySelector,
} from 'selectors/new-booking-selectors';
import { fetchWithTimeout } from '../../actions/fetch-helper';
import { injectUuidToPOI } from '../../helper/geo-helper';
import { buildCredentials } from '../../helper/session-helper';
import {
  enterpriseSelector,
  getUserCountryCodeFromState,
  isPaxSelector,
  quoteRequestConfigSelector,
  selectedCitySelector,
  userCompanyIDSelector,
  userSafeIDSelector,
} from '../../selectors/app-selector';
import {
  getErrorHttpStatus,
  getErrorMessage,
  isUnauthorizedError,
  logOutIfStatus401,
} from './common-errors';
import { getBatchQuoteUpdateByDeliveryIDsObservables } from './new-booking-epics-extensions';

const createDeliveriesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.CREATE_DELIVERIES),
    mergeMap(action => {
      const { deliveryIDs, skipDuplicateBookingCheckDeliveryIDs } = action;
      let { bookingMode } = action;
      const deliveries = deliveriesFromPropsSelector(state$.value, {
        deliveryIDs,
      });
      const isPax = isPaxSelector(state$.value);
      const countryCode = getUserCountryCodeFromState(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const paymentMethods = paymentMethodSelector(state$.value);
      const allTaxiTypes = serviceTypesInCitySelector(state$.value);
      if (bookingMode !== BookingMode.Bulk) {
        bookingMode =
          deliveryIDs.length > 1 ? BookingMode.Multi : BookingMode.Single;
      }

      const payload = deliveriesToCreateDeliveriesV2Payload({
        deliveries,
        paymentMethods,
        allTaxiTypes,
        enterprise,
        countryCode,
        bookingMode,
        skipDuplicateBookingCheckDeliveryIDs,
      });

      return forkJoin(
        of(undefined),
        of(deliveryIDs),
        of(bookingMode),
        ajax({
          url: endpoints.getCreateOrderV2Ept(),
          method: 'POST',
          headers: buildHeadersV2({
            isPax,
            companyID: _get(enterprise, 'companyID'),
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(isPax),
          body: JSON.stringify(payload),
          timeout: 10000,
        }).pipe(catchError(error => of({ error }))),
      );
    }),
    mergeMap(([precheckError, deliveryIDs, bookingMode, createResponse]) => {
      if (precheckError) {
        return of(
          NewBookingDuck.creators.finishCreatingDeliveries(
            undefined,
            deliveryIDs,
            precheckError,
            undefined,
            bookingMode,
          ),
        );
      }

      const { response, error } = createResponse;
      if (error || !response) {
        return of(
          logOutIfStatus401(error),
          NewBookingDuck.creators.finishCreatingDeliveries(
            undefined,
            deliveryIDs,
            error || new Error('no response'),
            undefined,
            bookingMode,
          ),
        );
      }

      const { deliveries = [] } = response;
      const successfulDeliveryIDs = [];
      const failedDeliveryIDs = [];
      const deliveryErrors = {};
      deliveries.forEach(delivery => {
        if (!(delivery.errors && delivery.errors.length > 0)) {
          successfulDeliveryIDs.push(delivery.createRequestID);
        } else {
          failedDeliveryIDs.push(delivery.createRequestID);
          deliveryErrors[delivery.createRequestID] = delivery.errors;
        }
      });

      return of(
        hydraScanStartAction('createDeliveries', response),
        NewBookingDuck.creators.finishCreatingDeliveries(
          successfulDeliveryIDs,
          failedDeliveryIDs,
          undefined,
          deliveryErrors,
          bookingMode,
        ),
      );
    }),
  );

const finishCreatingDeliveriesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.FINISH_CREATING_DELIVERIES),
    mergeMap(action => {
      const obs = [];

      const firstSuccessfulDeliveryID = _get(action, [
        'successfulDeliveryIDs',
        0,
      ]);
      const firstSuccessfulDelivery = deliverySelector(state$.value, {
        deliveryID: firstSuccessfulDeliveryID,
      });
      if (firstSuccessfulDelivery) {
        const city = selectedCitySelector(state$.value);
        const safeID = userSafeIDSelector(state$.value);
        if (city && safeID) {
          savePickUpPOIInStorage(_get(city, 'cityID'), safeID, {
            id: _get(firstSuccessfulDelivery, [
              'detail',
              'pickUp',
              'poi',
              'id',
            ]),
            latlng: _get(firstSuccessfulDelivery, [
              'detail',
              'pickUp',
              'poi',
              'latlng',
            ]),
          });
        }
      }

      const isPax = isPaxSelector(state$.value);
      const city = selectedCitySelector(state$.value);
      if (
        isPax &&
        (!action.error || getErrorHttpStatus(action.error) !== 401)
      ) {
        obs.push(getAllPaxPaymentMethodsV2(_get(city, 'countryCode')));
      }

      return of(...obs);
    }),
  );

const batchFetchDeliveryQuotesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.BATCH_FETCH_DELIVERY_QUOTES),
    mergeMap(action => {
      const { deliveryIDs } = action;
      const quoteRequestConfig = quoteRequestConfigSelector(state$.value);
      const enableChunking = _get(quoteRequestConfig, 'enableChunking', false);

      // Optimized flow
      if (enableChunking) {
        return getBatchQuoteUpdateByDeliveryIDsObservables(state$, {
          deliveryIDs,
          quoteRequestConfig,
        });
      }

      // Unoptimized flow
      const deliveries = deliveriesFromPropsSelector(state$.value, {
        deliveryIDs,
      });
      return forkJoin(
        of(deliveryIDs),
        ajaxFetchDeliveriesQuote(deliveries, state$.value),
      );
    }),
    mergeMap(([deliveryIDs, resp]) => {
      const error = _get(resp, 'error', '');
      const payloads = _get(resp, 'response.quotes', []);

      const pushDeliveryQuotesError = ({ message, description }) =>
        pushNotificationAction({
          id: 'quote-errors',
          type: NOTIFICATION_TYPE.WARNING,
          message,
          description,
        });

      const defaultUpdatedDeliveries = {};
      deliveryIDs.forEach((deliveryID, i) => {
        defaultUpdatedDeliveries[deliveryID] = {
          quote: undefined,
          fareDetailMatrix: undefined,
          config: undefined,
          paxExpressError: undefined,
        };
      });

      // return here only if unauthorized
      if (isUnauthorizedError(error)) {
        return of(
          logOutIfStatus401(error),
          pushDeliveryQuotesError({
            message: I18n.t('v2.error.quote.getQuoteFailedError.message'),
            description: getErrorMessage(error),
          }),
        );
      }

      const obs = [];

      // return if not all quotes returned, cannot continue if result count does not match
      if (payloads.length !== deliveryIDs.length) {
        const error = new Error('Some delivery quotes not returned');
        const errorCtx = rumErrorCtx(getDefaultErrorContext())
          .component('batchFetchDeliveryQuotesEpic')
          .severity(SEVERITY.MEDIUM)
          .custom('tags', {
            totalCount: deliveryIDs.length,
            returnedCount: payloads.length,
            notReturnedCount: deliveryIDs.length - payloads.length,
          })
          .build();
        ddRum.addError(error, errorCtx);
        return of(
          pushDeliveryQuotesError({
            message: I18n.t('v2.error.quote.getQuoteFailedError.message'),
            description: getErrorMessage(error),
          }),
        );
      }

      // show single error notification if any result fails, and then continue
      if (error) {
        const countByQuote = _countBy(
          payloads,
          quoteResponse => !!_get(quoteResponse, 'quote'),
        );
        const quoteError = new Error('Some deliveries without quotes');
        const errorCtx = rumErrorCtx(getDefaultErrorContext())
          .component('batchFetchDeliveryQuotesEpic')
          .severity(SEVERITY.MEDIUM)
          .custom('tags', {
            totalCount: deliveryIDs.length,
            successCount: countByQuote.true,
            failedCount: countByQuote.false,
          })
          .build();
        ddRum.addError(quoteError, errorCtx);
        obs.push(
          pushDeliveryQuotesError({
            message: I18n.t('v2.error.quote.getQuoteFailedError.message'),
            description: getErrorMessage(error),
          }),
        );
      }

      // update quote for even partial success
      const updatedDeliveries = {};
      deliveryIDs.forEach((deliveryID, i) => {
        updatedDeliveries[deliveryID] = {
          ...defaultUpdatedDeliveries[deliveryID],
          ...payloads[i], // order matters since response does not have deliveryID
        };

        // Show notification to Handle paxExpressError notifications for 1168 and 1169
        const paxExpressError = _get(payloads[i], 'paxExpressError');
        if (paxExpressError) {
          const { errorCode, title, message } = paxExpressError;
          if (isAreaZoningError(errorCode)) {
            const notificationType = isAreaZoningSoftBlock(errorCode)
              ? NOTIFICATION_TYPE.INFO
              : NOTIFICATION_TYPE.WARNING;

            obs.push(
              pushNotificationAction({
                id: `quote-error-${deliveryID}`,
                type: notificationType,
                message:
                  title || I18n.t('v2.error.quote.getQuoteFailedError.message'),
                description: message || '',
                errorCode, // Pass errorCode for custom notification handling
              }),
            );

            obs.push(
              ScribeDuck.creators.scribeEVRegulationEvent({
                eventName: SCRIBE_REGULATION_EVENTS.EV_REGULATION_SNACKBAR_VIEW,
                errorCode,
                title: title || message,
              }),
            );
          }
        }
      });

      // Update Route optimization
      deliveryIDs.forEach((deliveryID, i) => {
        const routeOptimizationSignature = _get(
          payloads[i],
          'quote.alternativeFare[0].signature',
        );
        if (routeOptimizationSignature) {
          const routeOptimizationResult = _get(
            payloads[i],
            'quote.alternativeFare[0].routeOptimization',
          );
          if (routeOptimizationResult) {
            const delivery = deliverySelector(state$.value, { deliveryID });
            const preventAutoApplyRO = _get(
              delivery,
              'routeOptimization.preventAutoApplyRO',
            );
            if (!preventAutoApplyRO) {
              obs.push(
                NewBookingDuck.creators.applyRouteOptimization(
                  deliveryID,
                  routeOptimizationResult,
                ),
              );
            }
          }
        }
      });
      obs.push(
        NewBookingDuck.creators.batchUpdateDeliveryQuotesAndConfigs(
          updatedDeliveries,
        ),
      );

      return of(...obs);
    }),
  );

const fetchDeliveryQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.FETCH_DELIVERY_QUOTE),
    mergeMap(action => {
      const { deliveryID } = action;
      const delivery = deliverySelector(state$.value, { deliveryID });
      return forkJoin(
        of(deliveryID),
        ajaxFetchDeliveriesQuote([delivery], state$.value),
      );
    }),
    mergeMap(([deliveryID, resp]) => {
      const { response, error } = resp;
      const pushDeliveryQuoteError = ({ message, description }) =>
        pushNotificationAction({
          id: `quote-error-${deliveryID}`,
          type: NOTIFICATION_TYPE.WARNING,
          message,
          description,
        });

      if (error) {
        return of(
          logOutIfStatus401(error),
          pushDeliveryQuoteError({
            message: I18n.t('v2.error.quote.getQuoteFailedError.message'),
            description: getErrorMessage(error),
          }),
          NewBookingDuck.creators.updateDeliveryQuoteAndConfig(deliveryID, {
            quote: undefined,
            fareDetailMatrix: undefined,
            config: undefined,
          }),
        );
      }

      const quotePayload = _get(response, 'quotes[0]');
      if (!quotePayload) {
        return of(
          pushDeliveryQuoteError({
            message: I18n.t('v2.error.quote.getQuoteFailedError.message'),
            description: I18n.t(
              'v2.error.quote.getQuoteFailedError.description.unknown',
            ),
          }),
          NewBookingDuck.creators.updateDeliveryQuoteAndConfig(deliveryID, {
            quote: undefined,
            fareDetailMatrix: undefined,
            config: undefined,
            paxExpressError: undefined,
          }),
        );
      }

      const payload = {
        config: undefined,
        fareDetailMatrix: undefined,
        quote: undefined,
        paxExpressError: undefined,
        ...quotePayload,
      };

      const paxExpressError = payload.paxExpressError;
      // avoid oow error -> will auto select a timeslot
      if (
        paxExpressError &&
        paxExpressError.errorCode !== ErrorCodes.OutOfWindow.code
      ) {
        const { errorCode, title, message } = paxExpressError;
        // use BE provided title and message with appropriate notification type
        const notificationType = isAreaZoningError(errorCode)
          ? isAreaZoningSoftBlock(errorCode)
            ? NOTIFICATION_TYPE.INFO
            : NOTIFICATION_TYPE.WARNING
          : NOTIFICATION_TYPE.WARNING;

        const actions = [
          pushNotificationAction({
            id: `quote-error-${deliveryID}`,
            type: notificationType,
            message: title,
            description: isAreaZoningError(errorCode)
              ? message || ''
              : `${message} [code:${errorCode}]`,
            errorCode, // Pass errorCode for custom notification handling
          }),
          NewBookingDuck.creators.updateDeliveryQuoteAndConfig(
            deliveryID,
            payload,
          ),
        ];

        if (isAreaZoningError(errorCode)) {
          actions.push(
            ScribeDuck.creators.scribeEVRegulationEvent({
              eventName: SCRIBE_REGULATION_EVENTS.EV_REGULATION_SNACKBAR_VIEW,
              errorCode,
              title: title || message,
            }),
          );
        }

        return of(...actions);
      }

      const obs = [
        NewBookingDuck.creators.updateDeliveryQuoteAndConfig(
          deliveryID,
          payload,
        ),
      ];

      const discountEligibilityError = _get(
        payload,
        'quote.discountEligibilityError',
        null,
      );
      if (discountEligibilityError) {
        obs.push(
          pushDeliveryQuoteError({
            message: I18n.t(
              'phrase.offers.mktplace_offers_heading_offer_cant_be_used_edge_case',
            ),
            description:
              _get(discountEligibilityError, 'localizedMessage') ||
              I18n.t('v2.error.invalidPromo'),
          }),
        );
      }

      const routeOptimizationSignature = _get(
        payload,
        'quote.alternativeFare[0].signature',
      );
      obs.push(
        ScribeDuck.creators.scribeExpressPOIFindShortestRouteResults(
          deliveryID,
          routeOptimizationSignature
            ? ROUTE_OPTIMIZE_STATUS_VALUE.OPTIMIZED_SUCCESS
            : ROUTE_OPTIMIZE_STATUS_VALUE.DID_NOT_OPTIMIZE,
        ),
      );
      if (routeOptimizationSignature) {
        const routeOptimizationResult = _get(
          payload,
          'quote.alternativeFare[0].routeOptimization',
        );
        if (routeOptimizationResult) {
          obs.push(
            NewBookingDuck.creators.applyRouteOptimization(
              deliveryID,
              routeOptimizationResult,
            ),
          );
        }
      }

      return of(...obs);
    }),
  );

const mapRebookStep = stepPOI => step => {
  const { instruction: locationNote, name, phoneNumber: phone } = _get(
    step,
    'contact',
    {},
  );
  const insuranceOption = _get(step, 'insuranceOption');
  const cod = _get(step, 'cashOnDelivery.amount');
  const poi = _get(stepPOI, 'resp.result');
  const uuid = _get(stepPOI, 'resp.uuid');
  injectUuidToPOI(poi, uuid);
  return {
    poi,
    name,
    phone,
    locationNote,
    cod,
    insuranceOption,
  };
};

const mapRebookConfiguration = configuration => {
  const { vehicleType, serviceType, specialServiceType } = configuration;
  return {
    serviceType,
    specialServiceType,
    vehicleType,
    additionalServices: [],
  };
};

const isValidPaymentMethodID = strID => {
  const numID = Math.floor(Number(strID));
  return numID !== Infinity && String(numID) === strID && numID > 0;
};

const mapRebookOtherDetails = (
  // webbooking
  { referenceCode, paymentMethod, service, noteToDriver },
  // configuration fetch from BE
  {
    serviceType,
    specialServiceType,
    vehicleType,
    paymentMethodID,
    parcelTotalSize,
  },
  // rebookSteps fetch from server
  steps,
) => {
  const itemCategory = _get(steps[1], 'itemCategory');

  const parcelWeight = steps.slice(1).reduce((accumulator, dropOff) => {
    const weight = _get(dropOff, ['itemInfo', 'weight'], 0);
    return accumulator + weight;
  }, 0);

  const cashTypeID =
    _get(paymentMethod, 'name') === 'CASH'
      ? _get(paymentMethod, 'paidBy') === 'SENDER'
        ? CASH_BY_SENDER
        : CASH_BY_RECIPIENT
      : undefined;
  const validPaymentMethodID = isValidPaymentMethodID(paymentMethodID)
    ? paymentMethodID
    : undefined;
  return {
    paymentTypeID: cashTypeID || validPaymentMethodID || undefined,
    parcelTotalSize: parcelTotalSize || undefined,
    itemWeight: parcelWeight || undefined,
    itemType:
      (!_isEmpty(itemCategory) && `${itemCategory.id}-${itemCategory.name}`) ||
      undefined,
    referenceCode: referenceCode || undefined,
    deliveryInstruction: noteToDriver || undefined,
    promoCode:
      (_get(service, 'isPromoApplied') && _get(service, 'promoCode')) ||
      undefined,
  };
};

const pathsToCheckRoundTrip = ['contact.name', 'contact.phone', 'place.id'];

const checkRoundTrip = steps => {
  if (!steps || steps.length < 3) {
    return false;
  }
  const pickUp = steps[0];
  const lastDropOff = steps[steps.length - 1];
  return _every(
    pathsToCheckRoundTrip,
    path => _get(pickUp, path) === _get(lastDropOff, path),
  );
};

const rebookWithCodeObservable = (booking, isPax, enterprise) => {
  const bookingSteps = [..._get(booking, 'steps', [])];
  const isRoundTrip = checkRoundTrip(bookingSteps);
  const code =
    bookingSteps.length > 2
      ? booking.deliveryID || booking.bookingCode
      : booking.orderID;
  if (isRoundTrip) {
    // pop the step to query location
    bookingSteps.pop();
  }
  return from(
    Promise.all([
      fetchWithTimeout(endpoints.rebookEndpoint(code), {
        timeout: 5000,
        method: 'GET',
        headers: buildHeadersV2({
          isPax,
          companyID: _get(enterprise, 'companyID'),
          includeInternalHeaders: true,
        }),
        credentials: buildCredentials(isPax),
      })
        .then(resp => resp.json())
        .catch(error => ({ error })),
      ...bookingSteps.map(step =>
        fetchAddressV2({
          keyword: `${_get(step, 'location.coordinates.latitude')},${_get(
            step,
            'location.coordinates.longitude',
          )}`,
          isPax,
          company: {
            id: _get(enterprise, 'companyID'),
            countryCode: _get(enterprise, 'countryCode'),
          },
          isReverse: true,
        }),
      ),
    ]).then(([rebookResponse, ...stepResolveResponses]) => {
      const rebookSteps = _get(rebookResponse, 'steps', []);
      if (!rebookSteps) {
        return undefined;
      }
      if (isRoundTrip) {
        rebookSteps.pop();
      }

      const configuration = _get(rebookResponse, 'configuration');
      if (!configuration || rebookSteps.length <= 0) {
        return undefined;
      }

      if (
        stepResolveResponses.length !== rebookSteps.length ||
        !_every(stepResolveResponses, stepResolveResponse =>
          _get(stepResolveResponse, 'resp.result'),
        )
      ) {
        return undefined;
      }

      const detail = mapRebookOtherDetails(booking, configuration, rebookSteps);
      detail.isRoundTrip = isRoundTrip;
      detail.pickUp = mapRebookStep(stepResolveResponses[0])(rebookSteps[0]);
      detail.dropOffs = rebookSteps
        .slice(1)
        .map((rebookStep, index) =>
          mapRebookStep(stepResolveResponses[index + 1])(rebookStep),
        );
      detail.configuration = mapRebookConfiguration(configuration);
      return detail;
    }),
  );
};

const rebookWithCodesEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.REBOOK_WITH_CODES),
    debounceTime(300),
    mergeMap(action => {
      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const bookingConfiguration = bookingConfigurationSelector(state$.value);
      const { bookings, rebookJobID } = action;
      return forkJoin(
        of(rebookJobID),
        of(bookingConfiguration),
        ...bookings.map(booking =>
          rebookWithCodeObservable(booking, isPax, enterprise),
        ),
      );
    }),
    mergeMap(([rebookJobID, bookingConfiguration, ...rebookDetails]) =>
      of(
        NewBookingDuck.creators.rebookWithDetails(
          rebookDetails,
          rebookJobID,
          bookingConfiguration,
        ),
      ),
    ),
  );

const rebookWithCodeEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.REBOOK),
    debounceTime(300),
    mergeMap(action => {
      const { webbooking, callback } = action;
      const { steps } = webbooking;
      if (!steps) {
        if (callback) {
          callback(new Error('fail to rebook: no delivery steps'));
        }
        return EMPTY;
      }

      const isPax = isPaxSelector(state$.value);
      const enterprise = enterpriseSelector(state$.value);
      const code =
        steps.length > 2
          ? webbooking.deliveryID || webbooking.bookingCode
          : webbooking.orderID;
      const isRoundTrip = checkRoundTrip(steps);
      if (isRoundTrip) {
        // pop the step to query location
        steps.pop();
      }

      const addressSearches = steps.map(step =>
        from(
          fetchAddressV2({
            keyword: `${_get(step, 'location.coordinates.latitude')},${_get(
              step,
              'location.coordinates.longitude',
            )}`,
            isPax,
            company: {
              id: _get(enterprise, 'companyID'),
              countryCode: _get(enterprise, 'countryCode'),
            },
            isReverse: true,
          }),
        ),
      );

      return forkJoin(
        of(callback),
        of(webbooking),
        of(isRoundTrip),
        ajax({
          url: endpoints.rebookEndpoint(code),
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID: _get(enterprise, 'companyID'),
            includeInternalHeaders: true,
          }),
          withCredentials: buildAjaxCredentials(isPax),
          timeout: 5000,
        }).pipe(catchError(error => of({ error }))),
        ...addressSearches,
      );
    }),
    mergeMap(
      ([
        callback,
        webbooking,
        isRoundTrip,
        { response, error },
        ...stepPOIs
      ]) => {
        const steps = _get(response, 'steps', []);
        if (isRoundTrip) {
          // pop the step to query fill details
          steps.pop();
        }

        const configuration = _get(response, 'configuration');
        if (error || !steps || !configuration || steps.length <= 0) {
          if (callback) {
            callback(error || new Error('no rebook response'));
          }
          return of(
            NewBookingDuck.creators.rebookFailed(),
            pushNotificationAction({
              id: 'fail-to-rebook',
              type: NOTIFICATION_TYPE.WARNING,
              message: 'Fail to rebook',
            }),
          );
        }

        if (!stepPOIs || stepPOIs.length !== steps.length) {
          if (callback) {
            callback(new Error('fail to get POIs'));
          }
          return of(
            NewBookingDuck.creators.rebookFailed(),
            pushNotificationAction({
              id: 'fail-to-rebook',
              type: NOTIFICATION_TYPE.WARNING,
              message: 'Fail to rebook',
            }),
          );
        }

        const bookingConfiguration = bookingConfigurationSelector(state$.value);

        const detail = mapRebookOtherDetails(webbooking, configuration, steps);
        detail.isRoundTrip = isRoundTrip;
        detail.pickUp = mapRebookStep(stepPOIs[0])(steps[0]);
        detail.dropOffs = steps
          .slice(1)
          .map((step, index) => mapRebookStep(stepPOIs[index + 1])(step));
        detail.configuration = mapRebookConfiguration(configuration);

        if (callback) {
          callback();
        }
        return of(
          NewBookingDuck.creators.rebookWithDetail(
            detail,
            bookingConfiguration,
          ),
        );
      },
    ),
  );

const filterOffers = data => {
  const { offers, allDeliveries, currentDelivery } = data;

  const currentOfferID = _get(
    currentDelivery,
    'detail.configuration.offerConfig.offerID',
  );

  offers.forEach(offer => {
    const { offerID, offerType } = offer;
    const isReward = isRewardOffer(offerType);

    const count = _get(offer, 'count', null);
    if (count === null) {
      offer.count = 1;
    }

    allDeliveries.forEach(delivery => {
      const offerConfig = _get(delivery, 'detail.configuration.offerConfig');
      if (
        offerConfig &&
        offerConfig.offerID &&
        offerConfig.offerID === offerID
      ) {
        if (isReward && offer.count > 0) {
          offer.count -= 1;
        }
      }
    });
  });

  return offers.filter(
    offer => offer.count > 0 || offer.offerID === currentOfferID,
  );
};

const fetchPromoEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.FETCH_OFFER_LIST),
    mergeMap(action => {
      const { deliveryID } = action;
      const delivery = deliverySelector(state$.value, { deliveryID });

      const pickUpPOI = _get(delivery, 'detail.pickUp.poi');
      const pickUplatlng = _get(pickUpPOI, 'latlng');

      if (!pickUplatlng) {
        return EMPTY;
      }

      const companyID = userCompanyIDSelector(state$.value);
      const isPax = isPaxSelector(state$.value);

      const offerConfig = _get(delivery, 'detail.configuration.offerConfig');
      const offerType = getOfferType(offerConfig);
      let fetchAutoOfferDetail;
      if (isAutoApplyOfferType(offerType)) {
        fetchAutoOfferDetail = fetchObservableUtil(
          endpoints.getV3OfferEndpoint(),
          {
            method: 'POST',
            headers: buildHeadersV2({
              isPax,
              companyID,
              includeInternalHeaders: true,
            }),
            credentials: buildCredentials(isPax),
            body: JSON.stringify({
              discountToken: offerConfig.discountToken,
              poiID: pickUpPOI.id,
              ...pickUplatlng,
            }),
          },
        );
      }

      return forkJoin(
        of(deliveryID),
        fetchObservableUtil(endpoints.fetchOffersEndpoint(), {
          method: 'POST',
          headers: buildHeadersV2({
            isPax,
            companyID,
            includeInternalHeaders: true,
          }),
          credentials: buildCredentials(isPax),
          body: JSON.stringify({
            ...pickUplatlng,
          }),
        }),
        fetchAutoOfferDetail || of(undefined),
      );
    }),
    mergeMap(([deliveryID, { offers = [] }, autoOfferDetail]) => {
      const allDeliveries = allDeliveriesSelector(state$.value);
      const delivery = deliverySelector(state$.value, { deliveryID });
      const offerConfig = _get(delivery, 'detail.configuration.offerConfig');
      let validOffers = offers;

      if (autoOfferDetail && autoOfferDetail.offer) {
        // If promo is discoverable, then we need to remove it first to avoid duplicate
        validOffers = offers.filter(
          offer => getOfferID(offer) !== getOfferID(autoOfferDetail.offer),
        );
        validOffers.unshift(autoOfferDetail.offer);
      } else if (offerConfig) {
        // Move the current offer to top
        validOffers = offers.filter(
          offer => getOfferID(offer) !== getOfferID(offerConfig),
        );
        validOffers.unshift(offerConfig);
      }
      const filteredOffers = filterOffers({
        offers: validOffers,
        allDeliveries,
        currentDelivery: delivery,
      });
      return of(
        NewBookingDuck.creators.updateOfferList(deliveryID, filteredOffers),
      );
    }),
  );

const inputPromoCodeEpic = (action$, state$) =>
  action$.pipe(
    ofType(NewBookingDuck.types.INPUT_PROMO_CODE),
    debounceTime(500),
    mergeMap(action => {
      const { deliveryID, promoCode } = action;
      const delivery = deliverySelector(state$.value, { deliveryID });
      const pickUplatlng = _get(delivery, 'detail.pickUp.poi.latlng');

      if (!pickUplatlng) {
        return EMPTY;
      }

      const companyID = userCompanyIDSelector(state$.value);
      const isPax = isPaxSelector(state$.value);

      return fetchObservableUtil(
        endpoints.fetchOffersByPromoCodeEndpoint({
          ...pickUplatlng,
          promoCode,
        }),
        {
          method: 'GET',
          headers: buildHeadersV2({
            isPax,
            companyID,
            includeInternalHeaders: true,
          }),
          credentials: buildCredentials(isPax),
        },
        { rethrow: true },
      ).pipe(
        mergeMap(({ offers = [] }) => {
          const allDeliveries = allDeliveriesSelector(state$.value);
          const delivery = deliverySelector(state$.value, { deliveryID });

          const filteredOffers = filterOffers({
            offers,
            allDeliveries,
            currentDelivery: delivery,
          });

          return of(
            NewBookingDuck.creators.updateOfferList(deliveryID, filteredOffers),
          );
        }),
        catchError(err => of({})),
      );
    }),
  );

export default combineEpics(
  createDeliveriesEpic,
  finishCreatingDeliveriesEpic,
  fetchDeliveryQuoteEpic,
  batchFetchDeliveryQuotesEpic,
  rebookWithCodeEpic,
  rebookWithCodesEpic,
  fetchPromoEpic,
  inputPromoCodeEpic,
);
